/**
 * Shimmer loading placeholders.
 *
 * These replace the centred spinners that listings, dashboards and reports used to show. A spinner
 * says "something is happening"; a skeleton says "a table with this shape is about to appear here",
 * holds the layout so nothing jumps when the data lands, and makes the wait feel shorter because the
 * page is already drawn.
 *
 * The sweep animation lives in `styles/globals.css` under `.gl-skeleton` (dark-mode aware, and
 * disabled under `prefers-reduced-motion`).
 */

/** One shimmering block. Size it with Tailwind — `<Skeleton className="h-4 w-32" />`. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span aria-hidden="true" className={`gl-skeleton block ${className}`} />;
}

/**
 * Varied bar widths so a block of placeholder rows reads as data rather than as a grid.
 *
 * Indexed deterministically by row+column, NOT randomised: a random width would be re-rolled on
 * every re-render and the skeleton would visibly twitch while it waits.
 */
const BAR_WIDTHS = ['w-full', 'w-3/4', 'w-5/6', 'w-2/3', 'w-4/5', 'w-1/2'];

/**
 * Placeholder rows for a `<table>` body — render INSIDE `<tbody>`, in place of the spinner row.
 *
 * `cols` should match the real column count so the shimmer lines up with the headers above it;
 * pass the same expression the empty-state row uses for `colSpan`.
 */
export function TableSkeleton({ rows = 8, cols, firstColNarrow = false }: {
  rows?: number;
  cols: number;
  /** True when column 0 is a checkbox/selection cell, so it gets a small square instead of a bar. */
  firstColNarrow?: boolean;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-t border-slate-100">
          {Array.from({ length: cols }).map((_, c) => (
            <td key={c} className="px-3 py-3">
              {firstColNarrow && c === 0
                ? <Skeleton className="h-3.5 w-3.5 rounded" />
                : <Skeleton className={`h-3.5 ${BAR_WIDTHS[(r + c * 2) % BAR_WIDTHS.length]}`} />}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** The value + sub-label inside a dashboard KPI card, while the figure is still loading. */
export function KpiValueSkeleton() {
  return (
    <div className="mt-1 space-y-1.5">
      <Skeleton className="h-7 w-24" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}

/**
 * A stand-in for a chart or a whole report body.
 *
 * Deliberately not a single grey slab: a header bar plus bars of differing heights reads as "a chart
 * is coming", where one large rectangle reads as a broken image.
 */
export function ChartSkeleton({ height = 'h-56', bars = 9 }: { height?: string; bars?: number }) {
  // Fixed, uneven heights — see the note on BAR_WIDTHS about not randomising.
  const heights = ['h-1/3', 'h-2/3', 'h-1/2', 'h-5/6', 'h-2/5', 'h-3/4', 'h-1/2', 'h-full', 'h-3/5'];
  return (
    <div className="w-full">
      <Skeleton className="h-4 w-40 mb-4" />
      <div className={`flex items-end gap-2 ${height}`}>
        {Array.from({ length: bars }).map((_, i) => (
          <Skeleton key={i} className={`flex-1 ${heights[i % heights.length]}`} />
        ))}
      </div>
    </div>
  );
}

/** Stacked lines for a card/panel body — `lines` short bars of varying width. */
export function BlockSkeleton({ lines = 4, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2.5 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3.5 ${BAR_WIDTHS[i % BAR_WIDTHS.length]}`} />
      ))}
    </div>
  );
}
